import { useMemo, useState, useEffect } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  useMutualFunds,
  useCreateMutualFund,
  useUpdateMutualFund,
  useDeleteMutualFund,
  useRecordNav,
} from '@/hooks/useMutualFunds';
import { useInvestmentTypes } from '@/hooks/useInvestmentTypes';
import { useFetchAccounts } from '@accountHooks';
import { useInvestmentCurrencyConversion } from '@/hooks/useInvestmentCurrencyConversion';
import { useCreateInvestmentTransaction } from '@/hooks/useInvestmentTransactions';
import { toast } from 'sonner';
import {
  PageHeaderSkeleton,
  PortfolioSummarySkeleton,
  DistributionChartSkeleton,
  AccountCardSkeleton,
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowDownToLine, ArrowUpFromLine, Plus, AlertCircle, LineChart, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { TypeSummaryCard } from '@/components/investment-summary/TypeSummaryCard';
import { InvestmentDistributionChart } from '@/components/investment-charts/InvestmentDistributionChart';
import { InvestmentTransactionForm } from '@/components/investment-transaction-form/InvestmentTransactionForm';
import { MutualFundForm } from '@/components/mutual-fund-form/MutualFundForm';
import { MutualFundCard } from '@/components/mutual-fund-card/MutualFundCard';
import { MutualFundNavForm } from '@/components/mutual-fund-nav-form/MutualFundNavForm';
import type { MutualFund, MutualFundFormData } from '@/types/mutualFund';
import type { InvestmentTransactionFormData } from '@/components/investment-transaction-form/InvestmentTransactionForm';

/**
 * MutualFundsPage displays and manages Mutual Fund investments.
 * Accessible at /investments/mutual-funds.
 * Supports NAV tracking, units/shares, fund category, risk rating,
 * and deposit/withdrawal transactions.
 */
export default function MutualFundsPage() {
  // ── Dialog state ──────────────────────────────────────────────────────────
  const [fundFormOpen, setFundFormOpen] = useState(false);
  const [transactionFormOpen, setTransactionFormOpen] = useState(false);
  const [navFormOpen, setNavFormOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [editingFund, setEditingFund] = useState<MutualFund | null>(null);
  const [deletingFundId, setDeletingFundId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: funds = [], isLoading: fundsLoading, error: fundsError } = useMutualFunds();
  const { data: investmentTypes = [], isLoading: typesLoading, error: typesError } = useInvestmentTypes();
  const { data: accounts = [] } = useFetchAccounts();

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createFund = useCreateMutualFund();
  const updateFund = useUpdateMutualFund();
  const deleteFund = useDeleteMutualFund();
  const recordNav = useRecordNav();
  const createTransaction = useCreateInvestmentTransaction();

  // ── Derived ───────────────────────────────────────────────────────────────
  const mutualFundTypeId = useMemo(
    () => investmentTypes.find((t) => t.name === 'Mutual Fund')?.id,
    [investmentTypes]
  );

  const baseCurrency = useMemo(() => funds[0]?.currency ?? 'USD', [funds]);

  const filteredFunds = useMemo(() => {
    if (!searchQuery) return funds;
    const lowerQuery = searchQuery.toLowerCase();
    return funds.filter(inv => 
      inv.fund_name?.toLowerCase().includes(lowerQuery) ||
      inv.fund_manager?.toLowerCase().includes(lowerQuery) ||
      inv.account_number?.toLowerCase().includes(lowerQuery) ||
      inv.member_number?.toLowerCase().includes(lowerQuery)
    );
  }, [funds, searchQuery]);

  const {
    selectedCurrency,
    setSelectedCurrency,
    convertedInvestments,
    totalNetWorth,
    isConverting,
    conversionError,
  } = useInvestmentCurrencyConversion({ baseCurrency, investments: filteredFunds });

  const isLoading = fundsLoading || typesLoading;
  const hasFunds = funds.length > 0;

  // ── Error toasts ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (fundsError) toast.error('Failed to load mutual funds. Please try again.');
  }, [fundsError]);

  useEffect(() => {
    if (typesError) toast.error('Failed to load investment types. Please try again.');
  }, [typesError]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAddFund = () => { setEditingFund(null); setFundFormOpen(true); };
  const handleEditFund = (fund: MutualFund) => { setEditingFund(fund); setFundFormOpen(true); };
  const handleCancelFundForm = () => { setFundFormOpen(false); setEditingFund(null); };

  const handleDeposit = () => { setTransactionType('deposit'); setTransactionFormOpen(true); };
  const handleWithdraw = () => { setTransactionType('withdrawal'); setTransactionFormOpen(true); };

  const handleSaveFund = async (data: MutualFundFormData) => {
    try {
      if (editingFund) {
        await updateFund.mutateAsync({ id: editingFund.id, ...data });
        toast.success('Mutual fund updated successfully');
      } else {
        await createFund.mutateAsync(data);
        toast.success('Mutual fund added successfully');
      }
      setFundFormOpen(false);
      setEditingFund(null);
    } catch (err) {
      console.error('Error saving mutual fund:', err);
      toast.error(editingFund ? 'Failed to update mutual fund' : 'Failed to add mutual fund');
    }
  };

  const handleSaveTransaction = async (data: InvestmentTransactionFormData) => {
    try {
      await createTransaction.mutateAsync(data as any);
      toast.success(
        `${data.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded successfully`
      );
      setTransactionFormOpen(false);
    } catch (err) {
      console.error('Error recording transaction:', err);
      toast.error(`Failed to record ${data.transaction_type}`);
    }
  };

  const handleSaveNav = async (data: {
    investment_account_id: string;
    nav_per_unit: number;
    nav_date: string;
  }) => {
    try {
      await recordNav.mutateAsync({
        investmentAccountId: data.investment_account_id,
        navPerUnit: data.nav_per_unit,
        navDate: data.nav_date,
      });
      toast.success('NAV recorded successfully');
      setNavFormOpen(false);
    } catch (err) {
      console.error('Error recording NAV:', err);
      toast.error('Failed to record NAV');
    }
  };

  const handleDeleteFund = (id: string) => setDeletingFundId(id);
  const handleCancelDelete = () => setDeletingFundId(null);
  const handleConfirmDelete = async () => {
    if (!deletingFundId) return;
    try {
      await deleteFund.mutateAsync(deletingFundId);
      toast.success('Mutual fund deleted successfully');
      setDeletingFundId(null);
    } catch (err) {
      console.error('Error deleting mutual fund:', err);
      toast.error('Failed to delete mutual fund');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6" aria-busy={isLoading} aria-live="polite">

        {/* Header */}
        {isLoading ? (
          <PageHeaderSkeleton />
        ) : (
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Mutual Funds</h1>
              <p className="text-sm text-muted-foreground sm:text-base">
                Track your mutual fund investments, NAV, and unit performance
              </p>
            </div>

            <div
              className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap w-full lg:w-auto mt-4 lg:mt-0"
              role="group"
              aria-label="Mutual Funds actions"
            >
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search funds..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <CurrencyToggle
                  baseCurrency={baseCurrency}
                  selectedCurrency={selectedCurrency}
                  onCurrencyChange={setSelectedCurrency}
                  balance={totalNetWorth}
                  showBalance={false}
                />
                <Button
                  variant="outline"
                  onClick={() => setNavFormOpen(true)}
                  disabled={!hasFunds}
                  aria-label="Record NAV for a mutual fund"
                  aria-disabled={!hasFunds}
                  className="min-h-[44px] w-full sm:w-auto"
                >
                  <LineChart className="mr-2 h-4 w-4" aria-hidden="true" />
                  NAV
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeposit}
                  disabled={!hasFunds}
                  aria-label="Deposit funds to mutual fund"
                  aria-disabled={!hasFunds}
                  className="min-h-[44px] w-full sm:w-auto"
                >
                  <ArrowDownToLine className="mr-2 h-4 w-4" aria-hidden="true" />
                  Deposit
                </Button>
                <Button
                  variant="outline"
                  onClick={handleWithdraw}
                  disabled={!hasFunds}
                  aria-label="Withdraw funds from mutual fund"
                  aria-disabled={!hasFunds}
                  className="min-h-[44px] w-full sm:w-auto"
                >
                  <ArrowUpFromLine className="mr-2 h-4 w-4" aria-hidden="true" />
                  Withdraw
                </Button>
                <Button
                  onClick={handleAddFund}
                  aria-label="Add new mutual fund investment"
                  className="min-h-[44px] w-full sm:w-auto"
                >
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Add
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Currency conversion alerts */}
        {isConverting && (
          <Alert role="status" aria-live="polite" aria-busy="true">
            <AlertDescription>Converting to {selectedCurrency}…</AlertDescription>
          </Alert>
        )}
        {conversionError && (
          <Alert variant="destructive" role="alert" aria-live="assertive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              Failed to convert currencies. Displaying amounts in original currencies.
            </AlertDescription>
          </Alert>
        )}

        {/* Summary + Distribution */}
        {isLoading ? (
          <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-3">
            <PortfolioSummarySkeleton />
            <DistributionChartSkeleton />
            <DistributionChartSkeleton />
          </div>
        ) : (
          <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-3">
            <TypeSummaryCard
              typeName="Mutual Funds"
              typeId={mutualFundTypeId ?? ''}
              investments={filteredFunds}
              currency={selectedCurrency}
              isLoading={isLoading}
              filterByType={false}
            />
            <InvestmentDistributionChart
              investments={convertedInvestments}
              currency={selectedCurrency}
              isLoading={isLoading}
            />
            <InvestmentDistributionChart
              investments={convertedInvestments}
              currency={selectedCurrency}
              isLoading={isLoading}
              groupBy="fund_category"
              title="Distribution by Category"
            />
          </div>
        )}

        {/* Fund cards / empty state */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <AccountCardSkeleton key={i} />
            ))}
          </div>
        ) : hasFunds ? (
          <div
            role="list"
            aria-label="Mutual fund investments"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {convertedInvestments.map((inv) => (
              <MutualFundCard
                key={inv.id}
                fund={inv as MutualFund}
                currency={selectedCurrency}
                onEdit={() => handleEditFund(inv as MutualFund)}
                onDelete={() => handleDeleteFund(inv.id)}
              />
            ))}
          </div>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-base sm:text-lg text-muted-foreground mb-2">
                No mutual fund investments yet
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Add your first mutual fund to start tracking NAV and unit performance
              </p>
              <Button
                onClick={handleAddFund}
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Add your first mutual fund investment"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add Fund
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Dialogs ── */}

        {/* Add / Edit Fund */}
        <Dialog open={fundFormOpen} onOpenChange={setFundFormOpen}>
          <DialogContent
            className="max-h-[90vh] overflow-y-auto sm:max-w-[640px] w-[95vw] sm:w-full"
            aria-describedby="fund-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {editingFund ? 'Edit Mutual Fund' : 'Add Mutual Fund'}
              </DialogTitle>
              <DialogDescription id="fund-dialog-description">
                {editingFund
                  ? 'Update the details of your mutual fund investment.'
                  : 'Add a new mutual fund to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <MutualFundForm
              mutualFund={editingFund}
              onSave={handleSaveFund}
              onCancel={handleCancelFundForm}
              isSubmitting={createFund.isPending || updateFund.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Deposit / Withdraw */}
        <Dialog open={transactionFormOpen} onOpenChange={setTransactionFormOpen}>
          <DialogContent
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="transaction-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {transactionType === 'deposit' ? 'Deposit Funds' : 'Withdraw Funds'}
              </DialogTitle>
              <DialogDescription id="transaction-dialog-description">
                {transactionType === 'deposit'
                  ? 'Record a deposit to your mutual fund. Enter the units purchased and NAV at time of purchase.'
                  : 'Record a withdrawal from your mutual fund.'}
              </DialogDescription>
            </DialogHeader>
            <InvestmentTransactionForm
              transactionType={transactionType}
              investments={funds}
              accounts={accounts}
              onSave={handleSaveTransaction}
              onCancel={() => setTransactionFormOpen(false)}
              isSubmitting={createTransaction.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Record NAV */}
        <Dialog open={navFormOpen} onOpenChange={setNavFormOpen}>
          <DialogContent
            className="sm:max-w-[480px] w-[95vw] sm:w-full"
            aria-describedby="nav-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>Record NAV</DialogTitle>
              <DialogDescription id="nav-dialog-description">
                Record the latest Net Asset Value for a mutual fund. Re-recording the same date updates the existing entry.
              </DialogDescription>
            </DialogHeader>
            <MutualFundNavForm
              funds={funds}
              onSave={handleSaveNav}
              onCancel={() => setNavFormOpen(false)}
              isSubmitting={recordNav.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <AlertDialog
          open={!!deletingFundId}
          onOpenChange={(open) => !open && handleCancelDelete()}
        >
          <AlertDialogContent
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="delete-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Mutual Fund</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This action cannot be undone. The fund, all NAV history, and associated
                transaction records will be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel
                onClick={handleCancelDelete}
                className="min-h-[44px] w-full sm:w-auto"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label="Confirm deletion of mutual fund"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
